import { useRef, useEffect, useCallback } from 'react';
import {
  createSimpleMarker,
  createDetailedMarker,
  getMarkerScaledSize,
  getMarkerAnchor,
} from '../../utils/markerUtils';
import { buildInfoWindowContent, InfoWindowOptions } from '../../utils/infoWindowContent';
import { useLogoCaching } from '../../pages/storesMap/useLogoCaching';
import { MapStore, MarkerEntry, ExportSelectionConfig } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get solution badge labels (P, CC, CM, S) from install dates */
const getSolutionLabels = (store: MapStore): string[] => {
  const labels: string[] = [];
  if (store.purchek_install_date) labels.push('P');
  if (store.containment_install_date) labels.push('CC');
  if (store.cart_manager_install_date) labels.push('CM');
  if (store.subscription_start_date) labels.push('S');
  return labels;
};

/** Is this store a customer? */
const isCustomer = (store: MapStore): boolean => {
  return !!(
    store.customer_id ||
    store.purchek_install_date ||
    store.containment_install_date ||
    store.cart_manager_install_date ||
    store.subscription_start_date ||
    store.paired_wheel_install_date
  );
};

/** Get recommended solutions from store.recommendations */
const getRecommendedSolutions = (store: MapStore): string[] => {
  const recs = store.recommendations;
  if (!recs) return [];
  const solutions: string[] = [];
  if (recs.purchek?.length > 0) solutions.push('Purchek');
  if (recs.containment?.length > 0) solutions.push('Cart Containment');
  if (recs.cartManager?.length > 0) solutions.push('Cart Manager');
  if (recs.subscription?.length > 0) solutions.push('Subscription');
  if (recs.pairedWheel?.length > 0) solutions.push('Paired Wheel');
  return solutions;
};

/** Get whitespace (solutions NOT installed) */
const getWhitespaceOpportunities = (store: MapStore): string[] => {
  const ws: string[] = [];
  if (!store.purchek_install_date) ws.push('Purchek');
  if (!store.containment_install_date) ws.push('Cart Containment');
  if (!store.cart_manager_install_date) ws.push('Cart Manager');
  if (!store.subscription_start_date) ws.push('Subscription');
  if (!store.paired_wheel_install_date) ws.push('Paired Wheel');
  return ws;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseMarkersOptions {
  map: google.maps.Map | null;
  stores: MapStore[];
  referenceStoreId?: string;
  showDetailedMarkers?: boolean;
  zoomThreshold?: number;
  fitBounds?: boolean;
  exportSelection?: ExportSelectionConfig;
  infoWindowOverrides?: Partial<InfoWindowOptions>;
  onStreetViewClick?: (position: { lat: number; lng: number }, storeName?: string) => void;
  onMarkerClick?: (store: MapStore) => void;
}

export function useMarkers(options: UseMarkersOptions) {
  const {
    map,
    stores,
    referenceStoreId,
    showDetailedMarkers = true,
    zoomThreshold = 13,
    fitBounds = false,
    exportSelection,
    infoWindowOverrides,
    onStreetViewClick,
    onMarkerClick,
  } = options;

  const markersRef = useRef<Map<string, MarkerEntry>>(new Map());
  const currentInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const { fetchBrandLogo } = useLogoCaching();

  // Keep callback refs to avoid stale closures
  const onStreetViewClickRef = useRef(onStreetViewClick);
  onStreetViewClickRef.current = onStreetViewClick;
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;
  const exportSelectionRef = useRef(exportSelection);
  exportSelectionRef.current = exportSelection;

  // -------------------------------------------------------------------
  // Build info window content for a store
  // -------------------------------------------------------------------
  const buildContent = useCallback(
    (store: MapStore, isRef: boolean) => {
      const isSelected = exportSelectionRef.current?.selectedIds.has(store.id) ?? false;
      return buildInfoWindowContent(
        {
          id: store.id,
          site: store.site,
          brand_name: store.brand_name,
          address: store.address,
          city: store.city ?? store.geo?.city,
          state: store.state ?? store.geo?.state_code,
          zipcode: store.zipcode,
          channel: store.channel,
          customer_id: store.customer_id,
          distance: store.distance,
          crime_rate: store.geo?.total_crime_incidents ?? null,
          purchek_install_date: store.purchek_install_date,
          containment_install_date: store.containment_install_date,
          cart_manager_install_date: store.cart_manager_install_date,
          subscription_start_date: store.subscription_start_date,
          paired_wheel_install_date: store.paired_wheel_install_date,
          plaza_cm_customer_count: store.nearby_penetration?.plaza_cm_customer_count ?? null,
          total_crime_amount_usd: store.total_crime_amount_usd ?? null,
          video_incident_count: store.video_incident_count ?? null,
          recommendation_rank: store.recommendation_rank ?? null,
          store_number: store.store_number ?? null,
          updated_store_ids: store.updated_store_ids ?? null,
        },
        {
          showStreetViewButton: true,
          showNeighbourhoodButton: !isRef,
          recommendedSolutions: getRecommendedSolutions(store),
          whitespaceOpportunities: getWhitespaceOpportunities(store),
          showExportToggle: !!exportSelectionRef.current,
          isSelectedForExport: isSelected,
          ...infoWindowOverrides,
        }
      );
    },
    [infoWindowOverrides]
  );

  // -------------------------------------------------------------------
  // Attach interactive listeners to info window DOM after it opens
  // -------------------------------------------------------------------
  const attachInfoWindowListeners = useCallback((store: MapStore) => {
    setTimeout(() => {
      // Street view button
      const svBtn = document.getElementById(`street-view-icon-btn-${store.id}`);
      if (svBtn) {
        svBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          onStreetViewClickRef.current?.(
            { lat: store.latitude, lng: store.longitude },
            store.site || store.brand_name
          );
        });
      }
      // Export toggle
      const exportBtn = document.getElementById(`export-toggle-btn-${store.id}`);
      if (exportBtn) {
        exportBtn.onclick = (e) => {
          e.stopPropagation();
          exportSelectionRef.current?.onToggle(store.id);
          const nowSelected = !exportSelectionRef.current?.selectedIds.has(store.id);
          const span = exportBtn.querySelector('span');
          if (span) span.textContent = nowSelected ? 'Remove from Export' : 'Add to Export';
          (exportBtn as HTMLElement).style.color = nowSelected ? '#dc2626' : '#3b82f6';
        };
      }
    }, 0);
  }, []);

  // -------------------------------------------------------------------
  // Create or update a single marker
  // -------------------------------------------------------------------
  const upsertMarker = useCallback(
    (store: MapStore, currentMap: google.maps.Map) => {
      const existing = markersRef.current.get(store.id);
      if (existing) return existing; // marker already on map

      const isCust = isCustomer(store);
      const labels = getSolutionLabels(store);
      const brandInit = (store.brand_name || '?').charAt(0).toUpperCase();
      const isRef = store.id === referenceStoreId;

      const currentZoom = currentMap.getZoom() || 4;
      const useDetailed = showDetailedMarkers && currentZoom >= zoomThreshold;

      const iconUrl = useDetailed
        ? createDetailedMarker(brandInit, isCust, '', labels, isRef)
        : createSimpleMarker(isCust);

      const svgHeight = labels.length > 0 ? 108 : 80;
      const size = getMarkerScaledSize(useDetailed, svgHeight);
      const anchor = getMarkerAnchor(useDetailed, labels.length > 0);

      const marker = new window.google.maps.Marker({
        position: { lat: store.latitude, lng: store.longitude },
        map: currentMap,
        title: store.site || store.brand_name || 'Store',
        zIndex: isRef ? 999 : 100,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(size.width, size.height),
          anchor: new window.google.maps.Point(anchor.x, anchor.y),
        },
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: buildContent(store, isRef),
      });

      const entry: MarkerEntry = {
        marker,
        infoWindow,
        store,
        labels,
        isCustomer: isCust,
        logoDataUrl: '',
        brandInitial: brandInit,
        isReference: isRef,
      };

      markersRef.current.set(store.id, entry);

      // Click handler
      marker.addListener('click', () => {
        if (currentInfoWindowRef.current) currentInfoWindowRef.current.close();
        // Rebuild content so export toggle state is fresh
        infoWindow.setContent(buildContent(store, isRef));
        marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
        infoWindow.open(currentMap, marker);
        currentInfoWindowRef.current = infoWindow;
        attachInfoWindowListeners(store);
        onMarkerClickRef.current?.(store);
      });

      // Async logo fetch
      if (store.brand_name) {
        fetchBrandLogo(store.brand_name).then((logoDataUrl) => {
          if (!logoDataUrl || !markersRef.current.has(store.id)) return;
          entry.logoDataUrl = logoDataUrl;

          // Only update icon if we're in detailed mode
          const z = currentMap.getZoom() || 4;
          if (showDetailedMarkers && z >= zoomThreshold) {
            const url = createDetailedMarker(brandInit, isCust, logoDataUrl, labels, isRef);
            marker.setIcon({
              url,
              scaledSize: new window.google.maps.Size(size.width, size.height),
              anchor: new window.google.maps.Point(anchor.x, anchor.y),
            });
          }
        }).catch(() => {});
      }

      return entry;
    },
    [referenceStoreId, showDetailedMarkers, zoomThreshold, buildContent, attachInfoWindowListeners, fetchBrandLogo]
  );

  // -------------------------------------------------------------------
  // Sync markers with the stores prop
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!map || !window.google) return;

    const currentStoreIds = new Set(stores.map((s) => s.id));

    // Remove markers for stores no longer in data
    markersRef.current.forEach((entry, id) => {
      if (!currentStoreIds.has(id)) {
        entry.marker.setMap(null);
        markersRef.current.delete(id);
      }
    });

    // Add new markers
    stores.forEach((store) => {
      if (!store.latitude || !store.longitude) return;
      upsertMarker(store, map);
    });

    // Fit bounds
    if (fitBounds && stores.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      stores.forEach((s) => {
        if (s.latitude && s.longitude) {
          bounds.extend({ lat: s.latitude, lng: s.longitude });
        }
      });
      map.fitBounds(bounds);
      // Don't zoom in too far for a single store
      const listener = map.addListener('idle', () => {
        if (map.getZoom()! > 15) map.setZoom(15);
        google.maps.event.removeListener(listener);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, stores, fitBounds]);

  // -------------------------------------------------------------------
  // Zoom-responsive marker restyling
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!map || !showDetailedMarkers) return;

    const listener = map.addListener('zoom_changed', () => {
      const z = map.getZoom() || 4;
      const useDetailed = z >= zoomThreshold;

      markersRef.current.forEach((entry) => {
        const { labels, isCustomer: isCust, brandInitial, logoDataUrl, isReference: isRef } = entry;
        const svgHeight = labels.length > 0 ? 108 : 80;
        const size = getMarkerScaledSize(useDetailed, svgHeight);
        const anchor = getMarkerAnchor(useDetailed, labels.length > 0);

        const iconUrl = useDetailed
          ? createDetailedMarker(brandInitial, isCust, logoDataUrl, labels, isRef)
          : createSimpleMarker(isCust);

        entry.marker.setIcon({
          url: iconUrl,
          scaledSize: new window.google.maps.Size(size.width, size.height),
          anchor: new window.google.maps.Point(anchor.x, anchor.y),
        });
      });
    });

    return () => google.maps.event.removeListener(listener);
  }, [map, showDetailedMarkers, zoomThreshold]);

  // -------------------------------------------------------------------
  // Cleanup all markers on unmount
  // -------------------------------------------------------------------
  useEffect(() => {
    const markers = markersRef.current;
    return () => {
      markers.forEach((entry) => entry.marker.setMap(null));
      markers.clear();
    };
  }, []);

  return { markersRef, currentInfoWindowRef };
}
